import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CheckCircle, Send, ArrowRight, GraduationCap, Target, LineChart, RefreshCw, Search, Shield, Building2, Plane, Clock, Phone, ShoppingBag, Trophy, Calculator, Code, BarChart, FileSearch, Sparkles, Users, Workflow, Zap, Award, TrendingUp, Star, Eye, BookOpen, MessageSquare } from 'lucide-react';
import { env } from './utils/env';
import SEOHead from './components/SEOHead';
import Header from './components/Header';
import Footer from './components/Footer';
import Hero from './components/Hero';
import ScrollToHash from "./components/ScrollToHash";
import About from './pages/About';
import Expertise from './pages/Expertise';
import CSR from './pages/CSR';
//import Careers from './pages/Careers';
//import Blog from './pages/Blog';
import Contact from './pages/Contact';
import Audit from './pages/Audit';
import Playbook from './pages/Playbook';
import CaseStudies from './pages/CaseStudies';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Legal from './pages/Legal';


const LOOP_START_FROM_END = 7; // en secondes : on boucle sur les 7 dernières secondes
const FALLBACK_LOOP_START = 3; // si la durée n'est pas connue, on utilisera 3s avant la fin


/* ========================
   Scroll management
======================== */
const ScrollToTopAndHash = () => {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (hash) {
      // Si on a un hash (#domains, etc.), on scroll vers l’élément
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Sinon, on remonte tout en haut
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
};

// Debug de la configuration (développement uniquement )
if (import.meta.env.DEV) {
  env.debugConfig();
}

// Page 404
const NotFound = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-16">
      <SEOHead 
        title="Page non trouvée — Neoqualis"
        description="La page que vous recherchez n'existe pas. Retournez à l'accueil ou explorez nos services de Testing et Business Analysis."
        canonical="https://neoqualis.com/404"
      />
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
        <h2 className="text-2xl font-semibold text-gray-700 mb-6">Page non trouvée</h2>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          La page que vous recherchez n'existe pas ou a été déplacée.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/"
            className="btn-primary inline-flex items-center"
          >
            Retour à l'accueil
            <ArrowRight className="ml-2" />
          </Link>
          <Link 
            to="/contact"
            className="btn-secondary inline-flex items-center"
          >
            Nous contacter
            <MessageSquare className="ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    societe: '',
    email: '',
    sujet: '',
    message: ''
  });

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Neoqualis",
    "description": "ESN française spécialisée en Testing (QA) et Business Analysis",
    "url": "https://neoqualis.com",
    "logo": "https://neoqualis.com/logo.png",
    "sameAs": [
      "https://linkedin.com/company/neoqualis",
      "https://instagram.com/neoqualis",
      "https://facebook.com/neoqualis"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Paris",
      "addressRegion": "Île-de-France",
      "addressCountry": "FR"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+33-1-86-04-61-82",
      "contactType": "customer service",
      "email": "contact@neoqualis.com"
    }
  };

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Audit QA & Testing",
    "description": "Audit complet de vos processus QA avec plan d'actions prioritaires",
    "provider": {
      "@type": "Organization",
      "name": "Neoqualis"
    },
    "serviceType": "Quality Assurance Audit",
    "areaServed": "FR"
  };

  const combinedJsonLd = [organizationJsonLd, serviceJsonLd];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/contact');
  };

  const expertises = [
    {
      title: 'Testing & QA',
      description: 'De la stratégie à l\'exécution : plans & cas de test, gestion des anomalies, web & mobile, tests de performance.',
      icon: Shield,
      color: 'primary'
    },
    {
      title: 'Business Analysis / PO',
      description: 'Recueil du besoin, formalisation (US/BRD), critères d\'acceptation, priorisation Agile, traçabilité exigences → tests.',
      icon: Users,
      color: 'secondary'
    },
    {
      title: 'Automatisation & CI/CD',
      description: 'Cypress • Selenium • Robot Framework : cadrage ROI, bibliothèques partagées, pipelines, reporting, déflakiness.',
      icon: Code,
      color: 'green-600'
    },
    {
      title: 'Performance',
      description: 'Stratégie de charge (JMeter), analyse des goulots, recommandations actionnables pour la scalabilité et la stabilité.',
      icon: BarChart,
      color: 'blue-600'
    },
    {
      title: 'Qualis-Academy',
      description: 'Formations Testing, BA et Agilité : programmes opérationnels, ateliers outils, coaching sur mission.',
      icon: GraduationCap,
      color: 'purple-600'
    },
    {
      title: 'QualisAI',
      description: 'Analyse de spécifications et génération de scénarios Gherkin avec export Xray/Squash et passerelles vers l\'automatisation.',
      icon: Sparkles,
      color: 'orange-600'
    }
  ];

  const methodology = [
    {
      step: 1,
      title: 'Cadrage & risques',
      description: 'Périmètre, objectifs, critères d\'acceptation, priorités et scénarios critiques.',
      icon: Search
    },
    {
      step: 2,
      title: 'Stratégie de test',
      description: 'Niveaux/types, jeux de données, environnements, outillage, intégration CI/CD.',
      icon: Target
    },
    {
      step: 3,
      title: 'Exécution & reporting',
      description: 'Plans & exécutions, KPI (couverture, défauts, vélocité), traçabilité bout-en-bout.',
      icon: LineChart
    },
    {
      step: 4,
      title: 'Amélioration continue',
      description: 'Boucles courtes, déflakiness automation, capitalisation et standards.',
      icon: RefreshCw
    }
  ];

  const sectors = [
    {
      title: 'Banque & Assurance',
      description: 'Parcours clients, conformité, sécurité, disponibilité.',
      icon: Building2,
      color: 'primary'
    },
    {
      title: 'Retraite & protection sociale',
      description: 'Référentiels complexes, qualité des données, calculs.',
      icon: Shield,
      color: 'secondary'
    },
    {
      title: 'Télécoms & Industrie',
      description: 'Scalabilité, performance, selfcare.',
      icon: Phone,
      color: 'green-600'
    },
    {
      title: 'Hôtellerie & services',
      description: 'Expérience omnicanale, réservation, fidélité.',
      icon: Trophy,
      color: 'blue-600'
    }
  ];

  const caseStudies = [
    {
      title: 'Parcours bancaire — fiabiliser un tunnel critique',
      description: 'Stratégie de tests + automation : + d\'anomalies avant MEP & - d\'incident après MEP, cycles de recette raccourcis.',
      icon: Building2,
      metrics: ['d\'anomalies', 'Cycles raccourcis', 'MEP sécurisée']
    },
    {
      title: 'Assurance IARD — montée en charge avant pic saisonnier',
      description: 'Plan de performance JMeter + tuning : temps de réponse ÷ 2, stabilité sous charge.',
      icon: Shield,
      metrics: ['Temps ÷ 2', 'Stabilité', 'Pic géré']
    },
    {
      title: 'Télécoms — traçabilité bout-en-bout',
      description: 'Xray/Squash + CI/CD : couverture mesurée, alertes précoces, go/no-go objectivé.',
      icon: Phone,
      metrics: ['Couverture', 'Alertes', 'Go/No-go']
    }
  ];

  const rseEngagements = [
    {
      title: 'Parité & inclusion',
      description: 'Mixité équilibrée (52 % / 48 %), intégration de jeunes talents (alternants & stagiaires) et nouveaux locaux accessibles PMR (accès, sanitaires, circulation).',
      icon: Users,
      color: 'primary'
    },
    {
      title: 'Numérique & achats responsables',
      description: 'Éco-gestes, hébergement sobre, suivi des consommations. Priorité aux équipements reconditionnés et seconde main (IT & mobilier), tri et réemploi avant achat neuf.',
      icon: Sparkles,
      color: 'green-600'
    },
    {
      title: 'QVT & sécurité',
      description: 'Mutuelle, titres resto, primes, proximité direction. Diagnostic QVT et DUERP en place ; prévention des risques & démarches bien-être.',
      icon: Shield,
      color: 'secondary'
    }
  ];

  const blogPosts = [
    {
      title: 'Automatiser sans fragiliser',
      description: 'Mesurer le ROI et réduire la flakiness.',
      icon: Code,
      readTime: '5 min'
    },
    {
      title: 'Xray vs Squash TM',
      description: 'Bien choisir et intégrer en CI/CD.',
      icon: Workflow,
      readTime: '7 min'
    },
    {
      title: 'JMeter',
      description: 'Erreurs courantes et quick-wins de performance.',
      icon: BarChart,
      readTime: '6 min'
    }
  ];

  return (
    <>
      <SEOHead 
        title="Neoqualis — ESN experte en Testing & Business Analysis"
        description="Testing fonctionnel & automatisé, performance, BA/PO. Réduisez les risques et accélérez vos mises en production avec l'expertise Neoqualis, QualisAI et Qualis-Academy."
        canonical="https://neoqualis.com/"
        jsonLd={combinedJsonLd}
      />
      {/* Hero Section */}
      <Hero
        src="/haro_neoqualis.mp4"
        poster="/hero-poster.webp"
      />
 

      {/* Nos expertises */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Nos expertises
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Une approche complète pour garantir la qualité de vos projets
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {expertises.map(({ title, description, icon: Icon, color }) => (
              <div key={title} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-6">
                  <div className={`p-3 rounded-full bg-${color} bg-opacity-10`}>
                    <Icon className={`text-${color} w-8 h-8`} />
                  </div>
                  <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                </div>
                <p className="text-gray-700">{description}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link 
              to="/expertise" 
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center"
            >
              Explorer nos solutions
              <ArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Méthode & gouvernance */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Méthode & Gouvernance
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Notre approche structurée en 4 étapes pour garantir le succès
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {methodology.map(({ step, title, description, icon: Icon }) => (
              <div key={step} className="bg-white rounded-xl shadow-lg p-6 text-center">
                <div className="flex items-center justify-center mb-4">
                  <div className="relative">
                    <div className="p-3 rounded-full bg-primary bg-opacity-10">
                      <Icon className="text-primary w-8 h-8" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {step}
                    </div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">{title}</h3>
                <p className="text-gray-700 text-sm">{description}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link 
              to="/playbook" 
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center"
            >
              Voir notre playbook QA
              <ArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Secteurs */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Nos secteurs d'intervention
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Une expertise adaptée à chaque domaine métier
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {sectors.map(({ title, description, icon: Icon, color }) => (
              <div key={title} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-full bg-${color} bg-opacity-10`}>
                    <Icon className={`text-${color} w-8 h-8`} />
                  </div>
                  <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                </div>
                <p className="text-gray-700">{description}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link 
              to="/expertise#domains" 
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center"
            >
              Découvrir nos domaines d'intervention
              <ArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Études de cas */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Études de cas
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Découvrez comment nous pouvons vous aider à réussir
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {caseStudies.map(({ title, description, icon: Icon, metrics }) => (
              <div key={title} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-primary bg-opacity-10">
                    <Icon className="text-primary w-8 h-8" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">{title}</h3>
                <p className="text-gray-700 mb-6">{description}</p>
                <div className="flex flex-wrap gap-2">
                  {metrics.map((metric) => (
                    <span 
                      key={metric}
                      className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full"
                    >
                      {metric}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link 
              to="/casestudies" 
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center"
            >
              Voir d'autres cas
              <ArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Innovation QualisAI */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-6">
              <Sparkles className="text-primary w-12 h-12" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
             Qualis AI — votre QA augmentée par l’intelligence artificielle
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Notre solution Qualis AI analyse vos spécifications (ambiguïtés, incohérences), génère 
              automatiquement des scénarios Gherkin et exporte vers Xray/Squash, Cypress, Selenium ou JMeter. 
              Résultat : meilleure couverture, cycles plus courts, moins de risques.
            </p>
            <Link 
              to="/contact?subject=QualisAI" 
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center"
            >
              Demander une démo de Qualis AI
              <Eye className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* RSE & engagements */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              RSE & Engagements
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Notre engagement pour un impact positif
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {rseEngagements.map(({ title, description, icon: Icon, color }) => (
              <div key={title} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-full bg-${color} bg-opacity-10`}>
                    <Icon className={`text-${color} w-8 h-8`} />
                  </div>
                  <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                </div>
                <p className="text-gray-700">{description}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link 
              to="/csr" 
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center"
            >
              Découvrir nos engagements
              <ArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Blog aperçu
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Blog & Actualités
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Nos derniers articles et conseils d'experts
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {blogPosts.map(({ title, description, icon: Icon, readTime }) => (
              <div key={title} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-primary bg-opacity-10">
                    <Icon className="text-primary w-8 h-8" />
                  </div>
                  <span className="ml-auto text-sm text-gray-500">{readTime}</span>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">{title}</h3>
                <p className="text-gray-700">{description}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link 
              to="/blog" 
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center"
            >
              Consulter le blog
              <BookOpen className="ml-2" />
            </Link>
          </div>
        </div>
      </section> */}

      {/* Bandeau final CTA */}
      <section className="py-16 bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Un go-live dans 8 semaines ? Parlons couverture et risques.
          </h2>
          <div className="flex flex-col gap-4 justify-center max-w-md mx-auto">
            <Link 
              to="/contact" 
              className="bg-white text-primary px-8 py-4 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center justify-center text-lg w-full"
            >
              Parler à un expert QA
              <MessageSquare className="ml-2" />
            </Link>
            <Link 
              to="/audit" 
              className="px-8 py-4 rounded-full font-semibold border border-white/80 bg-white/10 backdrop-blur hover:bg-white/20 transition-colors inline-flex items-center justify-center text-white text-lg w-full"
            >
              Demander un audit QA (offert — 30 min)
              <ArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

function App() {
  return (
    <div className="min-h-screen bg-white">
      <ScrollToTopAndHash />
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/expertise" element={<Expertise />} />
        <Route path="/csr" element={<CSR />} />
        {/* <Route path="/careers" element={<Careers />} /> */}
        {/* <Route path="/blog" element={<Blog />} /> */}
        <Route path="/contact" element={<Contact />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/playbook" element={<Playbook />} />
        <Route path="/casestudies" element={<CaseStudies />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default App;
